'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

/**
 * ReferenceAnnotator — quick freehand markup on a reference image.
 *
 * The user left-clicks a reference in the List chat and wants to scribble/circle/
 * mark it up to tell the agent what they mean. This is a SELF-CONTAINED widget:
 * it takes an image URL, lets the user draw (brush / eraser / colour / size /
 * undo / clear), and on "Done" composites the strokes ONTO the image and returns
 * a new File. The caller replaces the reference with that File, so the agent sees
 * the marked-up image as a `REFERENCE_IMAGE` (same path the crop modal uses).
 *
 * Kept isolated (its own canvas + pointer handling) so the drawing logic is simple
 * to reason about and never touches the crop modal's coordinate machinery.
 */

type ReferenceAnnotatorProps = {
  /** Object URL or /api/assets URL of the reference to annotate. */
  imageUrl: string;
  /** Name to give the produced File (kept close to the original). */
  fileName?: string;
  onCancel: () => void;
  onConfirm: (file: File) => void;
};

// Quick colour palette (bright, high-contrast against most photos).
const PALETTE = ['#FF3B30', '#FFCC00', '#34C759', '#0A84FF', '#FF2D55', '#FFFFFF', '#111111'] as const;
const SIZES = [6, 14, 28] as const;
// Cap the working resolution so blurs/strokes stay fast on 4K refs; the model
// doesn't need more than this for guidance anyway.
const MAX_WORK_DIM = 1600;
// Each undo step is a full-canvas ImageData snapshot (~w*h*4 bytes — up to ~10MB
// at 1600²). Keep the history short so a long session can't balloon memory.
const MAX_UNDO = 12;
const CANVAS_STYLE: React.CSSProperties = { cursor: 'crosshair' };

type Tool = 'brush' | 'eraser';

// The toolbar rows below are lists: each item owns its identity (tool / colour /
// size) and calls a single stable parent handler with it, so the parent never
// allocates a closure per item and React.memo actually holds.
const ToolBtn = React.memo(function ToolBtn({
  value,
  active,
  onSelect,
  label,
  children,
}: {
  value: Tool;
  active: boolean;
  onSelect: (tool: Tool) => void;
  label: string;
  children: React.ReactNode;
}) {
  const handleClick = useCallback(() => onSelect(value), [onSelect, value]);
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      title={label}
      className={clsx('grid h-7 w-7 place-items-center rounded-md text-sm', active ? 'bg-white/20' : 'hover:bg-white/10')}
    >
      {children}
    </button>
  );
});

const ColourBtn = React.memo(function ColourBtn({
  colour,
  active,
  onSelect,
}: {
  colour: string;
  active: boolean;
  onSelect: (colour: string) => void;
}) {
  const handleClick = useCallback(() => onSelect(colour), [onSelect, colour]);
  const style = useMemo(() => ({ backgroundColor: colour }), [colour]);
  return (
    <button
      type="button"
      aria-label={`colour ${colour}`}
      onClick={handleClick}
      className={clsx('h-6 w-6 rounded-full border transition-transform', active ? 'scale-110 border-white' : 'border-white/30')}
      style={style}
    />
  );
});

const SizeBtn = React.memo(function SizeBtn({
  size,
  active,
  onSelect,
}: {
  size: number;
  active: boolean;
  onSelect: (size: number) => void;
}) {
  const handleClick = useCallback(() => onSelect(size), [onSelect, size]);
  const dotStyle = useMemo(() => ({ width: size / 2 + 3, height: size / 2 + 3 }), [size]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className={clsx('grid h-7 w-7 place-items-center rounded-md', active ? 'bg-white/20' : 'hover:bg-white/10')}
      aria-label={`size ${size}`}
    >
      <span className="rounded-full bg-white" style={dotStyle} />
    </button>
  );
});

function ReferenceAnnotator({ imageUrl, fileName = 'annotated.webp', onCancel, onConfirm }: ReferenceAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null); // stroke layer (visible, transparent bg)
  const baseImgRef = useRef<HTMLImageElement | null>(null); // decoded source image
  const drawingRef = useRef(false);
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);
  const undoStackRef = useRef<ImageData[]>([]);

  const [ready, setReady] = useState(false);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [tool, setTool] = useState<'brush' | 'eraser'>('brush');
  const [color, setColor] = useState<string>(PALETTE[0]);
  const [size, setSize] = useState<number>(SIZES[1]);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load + decode the source, size the stroke canvas to the (capped) image dims.
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      const nw = img.naturalWidth || 1;
      const nh = img.naturalHeight || 1;
      const scale = Math.min(1, MAX_WORK_DIM / Math.max(nw, nh));
      const w = Math.max(1, Math.round(nw * scale));
      const h = Math.max(1, Math.round(nh * scale));
      baseImgRef.current = img;
      setDims({ w, h });
      setReady(true);
    };
    img.onerror = () => {
      if (!cancelled) onCancel();
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl, onCancel]);

  // Map a pointer event to stroke-canvas pixel coordinates (canvas intrinsic size
  // differs from its CSS display size, so scale by the ratio).
  const toCanvasPoint = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / Math.max(1, rect.width)) * canvas.width;
    const y = ((e.clientY - rect.top) / Math.max(1, rect.height)) * canvas.height;
    return { x, y };
  }, []);

  const strokeStyle = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = size;
      if (tool === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = color;
      }
    },
    [size, tool, color],
  );

  const pushUndo = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    try {
      undoStackRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
      if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift();
    } catch {
      /* getImageData can throw if tainted; skip undo snapshot rather than break drawing */
    }
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    canvas.setPointerCapture?.(e.pointerId);
    pushUndo();
    drawingRef.current = true;
    const p = toCanvasPoint(e);
    lastPtRef.current = p;
    // A single tap should leave a dot.
    strokeStyle(ctx);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.01, p.y + 0.01);
    ctx.stroke();
    setHasStrokes(true);
  }, [pushUndo, toCanvasPoint, strokeStyle]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const last = lastPtRef.current;
    if (!canvas || !ctx || !last) return;
    const p = toCanvasPoint(e);
    strokeStyle(ctx);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
  }, [toCanvasPoint, strokeStyle]);

  const endStroke = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPtRef.current = null;
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
  }, []);

  const undo = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const snap = undoStackRef.current.pop();
    if (!canvas || !ctx) return;
    if (snap) ctx.putImageData(snap, 0, 0);
    else ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Snapshots are the pre-stroke states; after restoring, the number left IS the
    // number of strokes still on the canvas (0 ⇒ back to the empty starting state).
    setHasStrokes(undoStackRef.current.length > 0);
  }, []);

  const clearAll = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    pushUndo();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  }, [pushUndo]);

  const confirm = useCallback(async () => {
    const strokeCanvas = canvasRef.current;
    const img = baseImgRef.current;
    if (!strokeCanvas || !img || !dims) return;
    // Nothing drawn: don't re-encode/downscale the pristine reference — just back
    // out, leaving the original untouched. (Done should mean "apply my markup".)
    if (!hasStrokes) {
      onCancel();
      return;
    }
    setBusy(true);
    try {
      // Composite: base image, then the strokes on top, at the working resolution.
      const out = document.createElement('canvas');
      out.width = dims.w;
      out.height = dims.h;
      const octx = out.getContext('2d');
      if (!octx) return;
      octx.drawImage(img, 0, 0, dims.w, dims.h);
      octx.drawImage(strokeCanvas, 0, 0, dims.w, dims.h);
      const encode = (type: string, quality?: number): Promise<Blob | null> =>
        new Promise((resolve) => out.toBlob((b) => resolve(b), type, quality));
      // WebP keeps it small, but some engines (older Safari) can't encode webp via
      // toBlob and return null — fall back to PNG so annotation never silently fails.
      let blob = await encode('image/webp', 0.92);
      let type = 'image/webp';
      if (!blob) {
        blob = await encode('image/png');
        type = 'image/png';
      }
      if (!blob) {
        // Both encoders failed (e.g. tainted canvas): surface it and stay put
        // rather than swallow the click and lose the user's markup.
        console.error('[ReferenceAnnotator] could not encode annotated image (toBlob returned null)');
        return;
      }
      const ext = type === 'image/png' ? '.png' : '.webp';
      const name = fileName.replace(/\.(png|jpe?g|webp)$/i, '') + ext;
      onConfirm(new File([blob], name, { type }));
    } finally {
      setBusy(false);
    }
  }, [dims, hasStrokes, fileName, onCancel, onConfirm]);

  const handleSelectTool = useCallback((next: Tool) => setTool(next), []);

  // Picking a colour implies you want to paint with it.
  const handleSelectColour = useCallback((next: string) => {
    setColor(next);
    setTool('brush');
  }, []);

  const handleSelectSize = useCallback((next: number) => setSize(next), []);

  return (
    <div className="flex h-full w-full flex-col gap-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg bg-white/[0.06] p-1">
          <ToolBtn value="brush" active={tool === 'brush'} onSelect={handleSelectTool} label="Draw">✏️</ToolBtn>
          <ToolBtn value="eraser" active={tool === 'eraser'} onSelect={handleSelectTool} label="Erase">🧽</ToolBtn>
        </div>
        <div className="flex items-center gap-1">
          {PALETTE.map((c) => (
            <ColourBtn key={c} colour={c} active={color === c && tool === 'brush'} onSelect={handleSelectColour} />
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-white/[0.06] p-1">
          {SIZES.map((s) => (
            <SizeBtn key={s} size={s} active={size === s} onSelect={handleSelectSize} />
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-[13px]">
          <button type="button" onClick={undo} className="rounded-md px-2 py-1 text-white/70 hover:bg-white/10">
            Undo
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={!hasStrokes}
            className="rounded-md px-2 py-1 text-white/70 hover:bg-white/10 disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Canvas stage. The image and the transparent stroke canvas are BOTH absolutely
          centred in this definite-size flex box and letterboxed by the same
          max-w/max-h plus their shared intrinsic aspect (img natural size; canvas
          width/height attrs), so they overlay pixel-for-pixel and toCanvasPoint stays
          correct. A relative wrapper with only absolutely-positioned children has no
          content to size from, so aspect-ratio cannot resolve and it collapses to 0x0 —
          which left nothing to draw on. */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-black/40">
        {ready && dims ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imageUrl}
              alt="reference"
              draggable={false}
              className="pointer-events-none absolute inset-0 m-auto h-auto w-auto max-h-full max-w-full select-none rounded-xl object-contain"
            />
            <canvas
              ref={canvasRef}
              width={dims.w}
              height={dims.h}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={endStroke}
              onPointerLeave={endStroke}
              onPointerCancel={endStroke}
              className="absolute inset-0 m-auto h-auto w-auto max-h-full max-w-full touch-none rounded-xl"
              style={CANVAS_STYLE}
            />
          </>
        ) : (
          <div className="text-sm text-white/50">Loading reference…</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm text-white/70 hover:bg-white/10">
          Cancel
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={busy || !ready}
          className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Use annotated reference'}
        </button>
      </div>
    </div>
  );
}

export default ReferenceAnnotator;
